<template>
  <div>
    <div class="mb-4">
      <h2 class="text-lg font-semibold text-gray-900">{{ $t('smm.settings.board_title') }}</h2>
      <p class="text-sm text-gray-500 mt-0.5">{{ $t('smm.settings.board_subtitle') }}</p>
    </div>

    <div class="flex flex-wrap gap-2 mb-4 border-b border-gray-100 pb-3">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        type="button"
        class="px-3 py-1.5 rounded-lg text-sm font-medium transition"
        :class="activeTab === tab.id
          ? 'bg-gray-900 text-white'
          : 'text-gray-600 hover:bg-gray-100'"
        @click="activeTab = tab.id"
      >
        {{ tab.label }}
      </button>
    </div>

    <p v-if="error" class="text-sm text-red-600 mb-3 whitespace-pre-line">{{ error }}</p>
    <div v-if="loading" class="text-sm text-gray-500 py-8 text-center">{{ $t('common.loading') }}</div>

    <div v-else class="grid grid-cols-1 xl:grid-cols-[240px_minmax(0,1fr)] gap-4">
      <div class="space-y-1">
        <button
          v-for="item in items"
          :key="item.id"
          type="button"
          class="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left border transition"
          :class="selectedId === item.id
            ? 'border-gray-900 bg-gray-900 text-white'
            : 'border-gray-200 hover:border-gray-400 bg-white text-gray-800'"
          @click="selectItem(item)"
        >
          <span
            v-if="item.color"
            class="w-3 h-3 rounded-full shrink-0 border border-black/10"
            :style="{ backgroundColor: item.color }"
          />
          <span class="truncate">{{ item.name }}</span>
          <span
            v-if="item.is_active === false"
            class="ml-auto text-[10px] uppercase opacity-70"
          >{{ $t('smm.settings.inactive') }}</span>
        </button>
        <p v-if="!items.length" class="text-sm text-gray-500 px-1 py-4">
          {{ $t('smm.settings.board_empty') }}
        </p>
        <button
          type="button"
          class="w-full mt-4 px-3 py-2 text-sm rounded-lg border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200"
          @click="createItem"
        >
          {{ $t('smm.settings.board_add') }}
        </button>
      </div>

      <div v-if="form" class="space-y-4">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label class="text-sm text-gray-600">{{ $t('common.name') }}</label>
            <input v-model="form.name" class="w-full border rounded-lg p-2.5 mt-1 text-sm" />
          </div>
          <div>
            <label class="text-sm text-gray-600">{{ $t('smm.settings.slug') }}</label>
            <input v-model="form.slug" class="w-full border rounded-lg p-2.5 mt-1 text-sm font-mono" />
          </div>
          <div v-if="activeTab !== 'sections'">
            <label class="text-sm text-gray-600">{{ $t('smm.settings.color') }}</label>
            <div class="flex items-center gap-2 mt-1">
              <input v-model="form.color" type="color" class="h-10 w-12 border rounded cursor-pointer bg-white" />
              <input v-model="form.color" type="text" class="flex-1 border rounded-lg p-2.5 text-sm font-mono" placeholder="#111827" />
            </div>
          </div>
          <div>
            <label class="text-sm text-gray-600">{{ $t('smm.settings.sort_order') }}</label>
            <input v-model.number="form.sort_order" type="number" min="0" class="w-full border rounded-lg p-2.5 mt-1 text-sm" />
          </div>
          <div v-if="activeTab === 'statuses'" class="sm:col-span-2 flex flex-wrap gap-4">
            <label class="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input v-model="form.is_done" type="checkbox" class="h-4 w-4 rounded border-gray-300" />
              {{ $t('smm.settings.board_is_done') }}
            </label>
          </div>
          <div v-if="activeTab === 'sections'">
            <label class="text-sm text-gray-600">{{ $t('smm.settings.board_field_type') }}</label>
            <select v-model="form.field_type" class="w-full border rounded-lg p-2.5 mt-1 text-sm">
              <option value="textarea">textarea</option>
              <option value="text">text</option>
            </select>
          </div>
          <div class="sm:col-span-2">
            <label class="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input v-model="form.is_active" type="checkbox" class="h-4 w-4 rounded border-gray-300" />
              {{ $t('smm.settings.is_active') }}
            </label>
          </div>
        </div>

        <div v-if="activeTab === 'projects' || activeTab === 'sections'">
          <label class="text-sm text-gray-600">{{ $t('common.description') }}</label>
          <textarea
            v-model="form.description"
            rows="4"
            class="w-full border rounded-lg p-2.5 mt-1 text-sm resize-y"
          />
        </div>

        <div class="flex flex-wrap gap-2 pt-2">
          <button
            type="button"
            class="px-4 py-2 text-sm rounded-lg bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
            :disabled="saving"
            @click="saveItem"
          >
            {{ saving ? $t('common.saving') : $t('common.save') }}
          </button>
          <button
            v-if="form.id"
            type="button"
            class="px-4 py-2 text-sm rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
            :disabled="saving"
            @click="deleteItem"
          >
            {{ $t('common.delete') }}
          </button>
        </div>
      </div>
      <p v-else class="text-sm text-gray-500 py-8">{{ $t('smm.settings.board_select') }}</p>
    </div>
  </div>
</template>

<script>
const ENDPOINTS = {
  projects: '/api/v1/smm/board/projects',
  statuses: '/api/v1/smm/board/statuses',
  types: '/api/v1/smm/board/types',
  sections: '/api/v1/smm/board/section-defs',
}

export default {
  data: () => ({
    activeTab: 'projects',
    items: [],
    selectedId: null,
    form: null,
    loading: false,
    saving: false,
    error: null,
    loadedTabs: {},
  }),
  computed: {
    tabs() {
      return [
        { id: 'projects', label: this.$t('smm.settings.board_tab_projects') },
        { id: 'statuses', label: this.$t('smm.settings.board_tab_statuses') },
        { id: 'types', label: this.$t('smm.settings.board_tab_types') },
        { id: 'sections', label: this.$t('smm.settings.board_tab_sections') },
      ]
    },
    endpoint() {
      return ENDPOINTS[this.activeTab]
    },
  },
  watch: {
    activeTab: {
      immediate: true,
      handler() {
        this.loadItems(true)
      },
    },
  },
  methods: {
    formatValidationError(e) {
      const data = e.response?.data
      if (!data) return null
      if (data.message) return data.message
      if (data.errors) return Object.values(data.errors).flat().join('\n')
      return null
    },
    blankForm() {
      const base = {
        id: null,
        name: '',
        slug: '',
        color: '#111827',
        description: '',
        sort_order: (this.items.length || 0) + 1,
        is_active: true,
      }
      if (this.activeTab === 'statuses') {
        return { ...base, is_done: false }
      }
      if (this.activeTab === 'sections') {
        return { ...base, field_type: 'textarea', color: undefined }
      }
      return base
    },
    async loadItems(resetSelection = false) {
      this.loading = true
      this.error = null
      try {
        const { data } = await axios.get(this.endpoint, { params: { all: 1 } })
        this.items = data.data || []
        if (resetSelection) {
          this.selectedId = this.items[0]?.id ?? null
          this.form = this.selectedId
            ? { ...this.items.find((i) => i.id === this.selectedId) }
            : null
        }
      } catch (e) {
        this.error = this.formatValidationError(e) || this.$t('common.error')
      } finally {
        this.loading = false
      }
    },
    selectItem(item) {
      this.selectedId = item.id
      this.form = { ...item }
      this.error = null
    },
    createItem() {
      this.selectedId = null
      this.form = this.blankForm()
      this.error = null
    },
    async saveItem() {
      if (!this.form) return
      this.saving = true
      this.error = null
      try {
        const payload = { ...this.form }
        delete payload.id
        delete payload.created_at
        delete payload.updated_at
        if (this.activeTab === 'sections') {
          delete payload.color
        }
        let res
        if (this.form.id) {
          res = await axios.patch(`${this.endpoint}/${this.form.id}`, payload)
        } else {
          res = await axios.post(this.endpoint, payload)
        }
        const saved = res.data.data
        await this.loadItems()
        this.selectItem(saved)
      } catch (e) {
        this.error = this.formatValidationError(e) || this.$t('common.error')
      } finally {
        this.saving = false
      }
    },
    async deleteItem() {
      if (!this.form?.id) return
      if (!window.confirm(this.$t('smm.settings.board_delete_confirm', { name: this.form.name }))) {
        return
      }
      this.saving = true
      this.error = null
      try {
        await axios.delete(`${this.endpoint}/${this.form.id}`)
        this.form = null
        this.selectedId = null
        await this.loadItems(true)
      } catch (e) {
        this.error = this.formatValidationError(e) || this.$t('common.error')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
